import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { AccountService } from "@/services/AccountService"
import { isCustomerDeceased } from "./getAccountContacts"
import { BillService } from "@/services/BillService"
import { MeterpointService } from "@/services/MeterpointService"
import { PaymentScheduleService } from "@/services/PaymentScheduleService"
import { TransactionService } from "@/services/TransactionService"
import { getSuggestedPayment } from "./getSuggestedPayment"
import { ITransaction } from "@/models/transaction/transaction.types"
import { mustLeave2MonthCreditAfterRefund } from "@/helpers/paymentHelper"
import dayjs from "dayjs"
import {
  REFUND_ELIGIBILITY_MAX_BILL_AGE,
  REFUND_MAX_BILL_AGE,
  REFUND_MAX_READING_AGE,
  REFUND_MIN_READING_AGE,
  REFUND_MIN_AMOUNT_IN_POUNDS,
  PAYMENT_AMOUNT_TOLERANCE,
} from "@/constants/finance"
import { DirectDebitService } from "@/services/DirectDebitService"
import { getUnderReviewPeriods } from "./getUnderReviewPeriods"

export enum RefundIssue {
  DIRTY_BILL_PRESENT = "DIRTY_BILL_PRESENT",
  NO_RECENT_READINGS = "NO_RECENT_READINGS",
  HAS_PARTIAL_RECENT_READINGS = "HAS_PARTIAL_RECENT_READINGS",
  NO_RECENT_BILLS = "NO_RECENT_BILLS",
  ACCOUNT_IN_DEBIT = "ACCOUNT_IN_DEBIT",
  ACCOUNT_BALANCE_TOO_LOW = "ACCOUNT_BALANCE_TOO_LOW",
  PAYMENT_AMOUNT_LOWER_THAN_SUGGESTED = "PAYMENT_AMOUNT_LOWER_THAN_SUGGESTED",
}

export enum RefundError {
  ACCOUNT_TERMINATING = "ACCOUNT_TERMINATING",
  CUSTOMER_DECEASED = "CUSTOMER_DECEASED",
  ACCOUNT_IN_DEBIT = "ACCOUNT_IN_DEBIT",
  ACCOUNT_BALANCE_LOWER_THAN_MIN_REFUND = "ACCOUNT_BALANCE_LOWER_THAN_MIN_REFUND",
  NO_ACTIVE_DIRECT_DEBIT = "NO_ACTIVE_DIRECT_DEBIT",
  ACCOUNT_UNDER_REVIEW = "ACCOUNT_UNDER_REVIEW",
  NO_RECENT_BILLS = "NO_RECENT_BILLS",
}

export interface IRefundEligibility {
  isEligible: boolean
  pendingRefund?: ITransaction
  maxRefundAmount: number
  mustLeave2MonthCredit: boolean
  setPaymentAmount: number
  error: RefundError | null
  toResolve: RefundIssue[]
  hasSmartMeterReadings: boolean
}

export const getRefundEligibility = (accountNumber: string) => {
  const queries = new QueryAggregator()
  const toResolve: RefundIssue[] = []
  let error: RefundError | null = null
  const readingAgeThreshold = dayjs().subtract(REFUND_MAX_READING_AGE, "day")
  const recentReadingAgeThreshold = dayjs().subtract(
    REFUND_MIN_READING_AGE,
    "day",
  )
  const billAgeThreshold = dayjs().subtract(REFUND_MAX_BILL_AGE, "day")
  const eligibilityBillAgeThreshold = dayjs().subtract(
    REFUND_ELIGIBILITY_MAX_BILL_AGE,
    "day",
  )

  const accountQuery = AccountService.getAccount(accountNumber)
  queries.add(accountQuery)

  const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
  queries.add(coreIdQuery)

  const transactionsQuery = TransactionService.getForAccount(coreIdQuery.data)
  queries.add(transactionsQuery)

  const meterpointsQuery = MeterpointService.getForAccount(accountNumber)
  queries.add(meterpointsQuery)

  const latestBillQuery = BillService.getLatestBill(coreIdQuery.data)
  queries.add(latestBillQuery)

  const allBillsQuery = BillService.getForAccount(coreIdQuery.data)
  queries.add(allBillsQuery)

  const unbilledReadingCostsQuery = AccountService.getUnbilledReadingCosts(
    coreIdQuery.data,
  )
  queries.add(unbilledReadingCostsQuery)

  const nextPaymentSchedule =
    PaymentScheduleService.getNextSchedule(accountNumber)
  queries.add(nextPaymentSchedule)

  const currentPaymentSchedule =
    PaymentScheduleService.getCurrentSchedule(accountNumber)
  queries.add(currentPaymentSchedule)

  const suggestedAmountQuery = getSuggestedPayment(accountNumber, true, true)
  queries.add(suggestedAmountQuery)

  const hasActiveDDQuery = DirectDebitService.hasActiveDirectDebit(
    coreIdQuery.data,
  )
  queries.add(hasActiveDDQuery)

  const isCustomerDeceasedQuery = isCustomerDeceased(accountNumber)
  queries.add(isCustomerDeceasedQuery)

  const reviewPeriodsQuery = getUnderReviewPeriods(accountNumber)
  queries.add(reviewPeriodsQuery)

  const passesEligibilityBillReqs = !!latestBillQuery.data?.periodFrom?.isAfter(
    eligibilityBillAgeThreshold,
    "day",
  )

  const passedBillReqs = !!latestBillQuery.data?.periodFrom?.isAfter(
    billAgeThreshold,
    "day",
  )

  const pendingRefund = transactionsQuery.data.find(
    (transaction) =>
      transaction.isRefund && transaction.statusText === "Pending",
  )

  const hasRecentReadings = meterpointsQuery.data.every((meterpoint) => {
    if (meterpoint.supplyStatus === "NotSupplied") {
      return true
    }
    return !!meterpoint.latestRead?.date?.isAfter(readingAgeThreshold, "day")
  })

  const isDirtyBill = allBillsQuery.data?.some((bill) => bill.isDirtyBill)

  const hasPartialRecentReadings = meterpointsQuery.data.every((meterpoint) => {
    if (meterpoint.supplyStatus === "NotSupplied") {
      return true
    }

    return !!meterpoint.latestRead?.date?.isAfter(
      recentReadingAgeThreshold,
      "day",
    )
  })

  const hasSmartMeterReadings = meterpointsQuery.data.every(
    (meterpoint) => meterpoint.latestSmartReadStatus?.type === "normal",
  )
  const inReview = reviewPeriodsQuery.data.some((period) => period.isActive)

  const accountBalance = accountQuery.data.balance
  const accountBalanceIncUnbilled =
    accountBalance + (unbilledReadingCostsQuery.data ?? 0)

  const paymentAmount =
    nextPaymentSchedule.data?.nextPayment ??
    currentPaymentSchedule.data?.nextPayment ??
    0

  const suggestedAmount = suggestedAmountQuery.data
  const mustLeave2MonthCredit =
    !nextPaymentSchedule.data?.isSeasonal && mustLeave2MonthCreditAfterRefund()

  const setPaymentAmount = paymentAmount * (mustLeave2MonthCredit ? 2 : 1)
  const maxRefundAmount = -accountBalanceIncUnbilled - setPaymentAmount

  // Errors
  if (accountQuery.data.isTerminating) {
    error = RefundError.ACCOUNT_TERMINATING
  } else if (isCustomerDeceasedQuery.data) {
    error = RefundError.CUSTOMER_DECEASED
  } else if (!hasActiveDDQuery.data) {
    error = RefundError.NO_ACTIVE_DIRECT_DEBIT
  } else if (accountBalance > 0) {
    error = RefundError.ACCOUNT_IN_DEBIT
  } else if (inReview) {
    error = RefundError.ACCOUNT_UNDER_REVIEW
  } else if (accountBalance > REFUND_MIN_AMOUNT_IN_POUNDS) {
    error = RefundError.ACCOUNT_BALANCE_LOWER_THAN_MIN_REFUND
  } else if (!passesEligibilityBillReqs) {
    error = RefundError.NO_RECENT_BILLS
  }

  // To Resolve
  if (accountBalanceIncUnbilled > 0) {
    toResolve.push(RefundIssue.ACCOUNT_IN_DEBIT)
  }

  if (!hasRecentReadings) {
    toResolve.push(RefundIssue.NO_RECENT_READINGS)
  }

  if (isDirtyBill) {
    toResolve.push(RefundIssue.DIRTY_BILL_PRESENT)
  }

  if (!hasPartialRecentReadings) {
    toResolve.push(RefundIssue.HAS_PARTIAL_RECENT_READINGS)
  }

  if (!passedBillReqs) {
    toResolve.push(RefundIssue.NO_RECENT_BILLS)
  }

  if (suggestedAmount - paymentAmount > PAYMENT_AMOUNT_TOLERANCE) {
    toResolve.push(RefundIssue.PAYMENT_AMOUNT_LOWER_THAN_SUGGESTED)
  }

  if (maxRefundAmount < REFUND_MIN_AMOUNT_IN_POUNDS) {
    toResolve.push(RefundIssue.ACCOUNT_BALANCE_TOO_LOW)
  }

  const data: IRefundEligibility = {
    isEligible: !error && !pendingRefund,
    maxRefundAmount: Math.max(maxRefundAmount, 0),
    mustLeave2MonthCredit,
    setPaymentAmount,
    pendingRefund,
    toResolve,
    error,
    hasSmartMeterReadings,
  }

  return queries.setData(data).getAggregate()
}
